/**
 * ConnectionsPage (/connections) — how agents and apps reach this workspace
 * as the signed-in user: API keys, connected OAuth apps (MCP connectors),
 * MCP setup help, and links to the AI provider and calendars/channels.
 *
 * Authority is the user's current access ∩ the connection's scope ∩ the
 * operation's policy (handoff §7); this page shows what the server enforces.
 * /profile?tab=apikeys redirects here (routes/workspaceRoutes.tsx).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plug, Bot, CalendarDays } from 'lucide-react';
import { ApiKeyManager } from '../../components/ApiKeyManager';
import { apiBase, mcpJsonSnippet } from '../../components/mcpSetup';
import { listOAuthGrants, revokeOAuthGrant, type OAuthGrant } from '../../packages/api-client/oauth-grants-api';
import { getErrorMessage } from '../../packages/api-client';
import { useHasPermission } from '../../packages/permission-guard';
import { Dialog, PageHeader, StateView } from '../../packages/ui';

const card: React.CSSProperties = {
    background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)',
};
const h2: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 'var(--space-2)', margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 700 };
const muted: React.CSSProperties = { margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' };
const pre: React.CSSProperties = {
    margin: 0, fontSize: 12, background: 'var(--color-surface-2)', padding: '10px 12px', borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border)', overflowX: 'auto', whiteSpace: 'pre-wrap',
};

const fmt = (iso: string) => new Date(iso).toLocaleString();

// An OAuth token's only scope is "mcp", which the server runs with the
// read+write default across every project the user can open (apikeys.DefaultScopes).
const REVOKE_BOUND = 'An access token it already holds keeps working for up to 15 minutes, until it expires.';

function ConnectedApps() {
    const [grants, setGrants] = useState<OAuthGrant[] | null>(null);
    const [loadError, setLoadError] = useState(false);
    const [pending, setPending] = useState<OAuthGrant | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const load = useCallback(() => {
        setLoadError(false);
        setGrants(null);
        listOAuthGrants().then(setGrants).catch(() => setLoadError(true));
    }, []);
    useEffect(load, [load]);

    async function disconnect() {
        if (!pending) return;
        const g = pending;
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            await revokeOAuthGrant(g.client_id);
            setGrants((prev) => (prev ?? []).filter((x) => x.client_id !== g.client_id));
            setNotice(`${g.client_name} disconnected: it can't get new tokens. ${REVOKE_BOUND}`);
        } catch (err) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status === 404) {
                // No live grant left server-side — already disconnected elsewhere.
                setGrants((prev) => (prev ?? []).filter((x) => x.client_id !== g.client_id));
                setNotice(`${g.client_name} was already disconnected.`);
            } else {
                setError(`Couldn't disconnect ${g.client_name}: ${getErrorMessage(err)}`);
            }
        } finally {
            setBusy(false);
            setPending(null);
        }
    }

    return (
        <section aria-labelledby="connected-apps-h" style={card}>
            <h2 id="connected-apps-h" style={h2}><Plug size={18} aria-hidden="true" /> Connected apps</h2>
            <p style={muted}>
                Apps you approved through the KaziAtlas sign-in screen, such as a claude.ai connector. Each acts as you with read and write access to every project you can open — never more than your own current access.
            </p>
            {error && <div role="alert" style={{ ...muted, color: 'var(--color-danger)' }}>{error}</div>}
            {notice && <div role="status" style={muted}>{notice}</div>}
            {loadError ? (
                <StateView state="error" message="Connected apps couldn't be loaded." onRetry={load} />
            ) : grants === null ? (
                <StateView state="loading" label="Loading connected apps" lines={2} />
            ) : grants.length === 0 ? (
                <StateView state="empty" title="No connected apps" message="Apps appear here after you approve them on the KaziAtlas sign-in screen." />
            ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    {grants.map((g) => (
                        <li key={g.client_id} aria-label={g.client_name} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{g.client_name}</div>
                                <div style={{ fontSize: 'var(--font-size-xs)', marginTop: 2 }}>
                                    <span>Read and write</span>{' · '}<span>All projects you can open</span>
                                </div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                                    <span>Connected {fmt(g.registered_at)}</span>{' · '}<span>Last token refresh {fmt(g.last_refreshed_at)}</span>
                                </div>
                            </div>
                            <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--color-danger)' }} disabled={busy} onClick={() => setPending(g)}>
                                Disconnect
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <Dialog
                open={pending !== null}
                onClose={() => { if (!busy) setPending(null); }}
                title={`Disconnect ${pending?.client_name ?? ''}?`}
                footer={
                    <>
                        <button type="button" className="btn btn-secondary btn-md" disabled={busy} onClick={() => setPending(null)}>Cancel</button>
                        <button type="button" className="btn btn-danger btn-md" disabled={busy} onClick={disconnect}>Disconnect</button>
                    </>
                }
            >
                <p>It stops getting new access tokens. {REVOKE_BOUND} To reconnect later, add it again from the app.</p>
            </Dialog>
        </section>
    );
}

function SetupHelp() {
    const base = apiBase();
    return (
        <section aria-labelledby="mcp-setup-h" style={card}>
            <h2 id="mcp-setup-h" style={h2}><Bot size={18} aria-hidden="true" /> Connect an MCP client</h2>
            <p style={muted}>MCP server URL: <code>{base}/mcp</code></p>
            <p style={muted}>
                <strong>Claude Code (API key):</strong> create a key above, then merge this into <code>~/.claude.json</code>, with your token in place of <code>kz_live_&lt;your-token&gt;</code>. The new key's panel has a copy with the token filled in.
            </p>
            <pre style={pre}>{mcpJsonSnippet('kz_live_<your-token>', base)}</pre>
            <p style={muted}>
                Use <code>headersHelper</code>, not <code>headers</code>: Claude Code runs OAuth discovery on servers configured with <code>headers</code> and would mark this one “not authenticated”.
                Restart Claude Code and run <code>/mcp</code> — <strong>kaziatlas</strong> should be listed as connected.
            </p>
            <p style={muted}>
                <strong>OAuth connectors</strong> (such as claude.ai): add the MCP server URL as a custom connector and approve it on the KaziAtlas sign-in screen. It then appears under Connected apps.
            </p>
            <p style={muted}>A copied snippet doesn't prove the connection works: a key's “Last used” updates when the server sees its first call.</p>
        </section>
    );
}

export function ConnectionsPage() {
    const canManageAI = useHasPermission('users', 'update');
    return (
        <div className="page" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', maxWidth: 880 }}>
            <PageHeader title="Connections" subtitle="Agents, apps and services that act as you in this workspace." />
            <section style={card} aria-label="API keys">
                <ApiKeyManager />
            </section>
            <ConnectedApps />
            <SetupHelp />
            <section aria-labelledby="other-conn-h" style={card}>
                <h2 id="other-conn-h" style={h2}><CalendarDays size={18} aria-hidden="true" /> Other connections</h2>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 'var(--font-size-sm)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    <li><Link to="/profile?tab=assistant">Calendars &amp; channels</Link> — Google/iCloud calendars, email and Telegram briefings (assistant settings).</li>
                    {canManageAI && <li><Link to="/admin/ai">AI provider</Link> — the workspace's Anthropic key for AI features.</li>}
                </ul>
            </section>
        </div>
    );
}
